import React, {
  Suspense,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Canvas } from "@react-three/fiber";
import { Environment, OrthographicCamera, Loader } from "@react-three/drei";
import Avatar from "./components/Avatar";
import AuthWrapper from "./components/AuthWrapper";
import OAuthCallback from "./components/OAuthCallback";
import SilentCallback from "./components/SilentCallback";
import oauthService from "./services/oauth.service";
import "./App.css";

// Add this inline style object for basic layout
const styles = {
  container: {
    display: "flex",
    width: "100%",
    height: "100vh",
  },
  avatarSection: {
    width: "50%",
    height: "100%",
    position: "relative",
  },
  uiSection: {
    width: "50%",
    height: "100%",
    overflowY: "auto",
    backgroundColor: "#f0f1f7",
    padding: "20px",
  },
  card: {
    maxWidth: "100%",
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
    padding: "20px",
  },
  heading: {
    fontSize: "24px",
    fontWeight: "bold",
    textAlign: "center",
    color: "#6445b7",
    marginBottom: "16px",
  },
  timer: {
    fontSize: "20px",
    fontWeight: "bold",
    textAlign: "center",
    backgroundColor: "#f5f5f5",
    padding: "8px 16px",
    borderRadius: "4px",
    marginBottom: "16px",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "center",
    gap: "16px",
    marginBottom: "16px",
  },
  button: {
    padding: "8px 16px",
    borderRadius: "4px",
    fontWeight: "bold",
    color: "white",
    cursor: "pointer",
    border: "none",
  },
  startButton: {
    backgroundColor: "#4cae4f",
  },
  stopButton: {
    backgroundColor: "#e74c3c",
  },
  repeatButton: {
    backgroundColor: "#3498db",
  },
  logoutButton: {
    backgroundColor: "#95a5a6",
  },
  disabledButton: {
    backgroundColor: "#cccccc",
    cursor: "not-allowed",
    opacity: 0.7,
  },
  listeningStatus: {
    textAlign: "center",
    fontWeight: "bold",
    color: "#e74c3c",
    marginBottom: "16px",
  },
  questionBox: {
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    padding: "16px",
    backgroundColor: "#f9f9f9",
    marginBottom: "16px",
  },
  answerGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
    marginBottom: "16px",
  },
  answerBox: {
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    padding: "16px",
    backgroundColor: "#f9f9f9",
  },
  boxTitle: {
    fontWeight: "bold",
    color: "#6445b7",
    borderBottom: "1px solid #e0e0e0",
    paddingBottom: "8px",
    marginBottom: "8px",
  },
  statusText: {
    textAlign: "center",
    fontStyle: "italic",
    color: "#666",
    marginBottom: "8px",
  },
  scoreDisplay: {
    textAlign: "center",
    fontWeight: "bold",
    fontSize: "20px",
    backgroundColor: "#f5f5f5",
    padding: "8px 16px",
    borderRadius: "4px",
  },
  userInfo: {
    textAlign: "center",
    padding: "10px",
    backgroundColor: "#e8f4f8",
    borderRadius: "6px",
    marginBottom: "16px",
    fontSize: "14px",
    color: "#2c3e50",
  },
};

function ScienceBeeApp() {
  // Avatar state
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [currentText, setCurrentText] = useState("");

  // Science Bee UI state
  const [timer, setTimer] = useState("00:00:00");
  const [isGameActive, setIsGameActive] = useState(false);
  const [listeningStatus, setListeningStatus] = useState("");
  const [question, setQuestion] = useState("");
  const [userAnswer, setUserAnswer] = useState("None");
  const [correctAnswer, setCorrectAnswer] = useState("None");
  const [score, setScore] = useState("0/0");
  const [status, setStatus] = useState("");

  // OAuth - Current user state
  const [currentUser, setCurrentUser] = useState(null);

  // URL parameters state
  const [queryParams, setQueryParams] = useState({
    grade: "9", // Default value
    topic: "", // Default empty
  });

  // Refs
  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioSourceRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isPlayingAudioRef = useRef(false);
  const isRecordingRef = useRef(false);
  const ucidRef = useRef(Date.now().toString());
  const packetCounterRef = useRef(0);
  const processorRef = useRef(null);
  const inputStreamRef = useRef(null);
  const globalStreamRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const secondsRef = useRef(0);
  const minutesRef = useRef(0);
  const hoursRef = useRef(0);
  const stopGameRef = useRef(null); // Ref to avoid circular dependencies

  // OAuth - Load current user on mount
  useEffect(() => {
    const user = oauthService.getUser();
    setCurrentUser(user);
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const grade = urlParams.get("grade") || "9"; // Default to grade 9 if not specified
    const topic = urlParams.get("topic") || ""; // Default to empty if not specified

    setQueryParams({ grade, topic });
    debugLog(`URL parameters loaded: grade=${grade}, topic=${topic}`);
  }, []);

  // Debug logging
  const debugLog = useCallback((message) => {
    const timestamp = new Date().toISOString();
    console.log(`${timestamp}: ${message}`);
  }, []);

  // Update status message
  const updateStatus = useCallback(
    (message) => {
      setStatus(message);
      debugLog(`Status update: ${message}`);
    },
    [debugLog]
  );

  // Timer functions
  const updateTimer = useCallback(() => {
    secondsRef.current++;
    if (secondsRef.current >= 60) {
      secondsRef.current = 0;
      minutesRef.current++;
      if (minutesRef.current >= 60) {
        minutesRef.current = 0;
        hoursRef.current++;
      }
    }
    setTimer(
      `${String(hoursRef.current).padStart(2, "0")}:${String(
        minutesRef.current
      ).padStart(2, "0")}:${String(secondsRef.current).padStart(2, "0")}`
    );
  }, []);

  const startTimer = useCallback(() => {
    timerIntervalRef.current = setInterval(updateTimer, 1000);
  }, [updateTimer]);

  const stopTimer = useCallback(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    secondsRef.current = 0;
    minutesRef.current = 0;
    hoursRef.current = 0;
    setTimer("00:00:00");
  }, []);

  // Audio encoding function
  const encodePCM16 = useCallback((buffer) => {
    const length = buffer.length * 2;
    const buffer16 = new ArrayBuffer(length);
    const view = new DataView(buffer16);
    let offset = 0;
    for (let i = 0; i < buffer.length; i++, offset += 2) {
      let s = Math.max(-1, Math.min(1, buffer[i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
    return buffer16;
  }, []);

  // Recording controls
  const startRecording = useCallback(() => {
    isRecordingRef.current = true;
    setListeningStatus("Listening for your answer...");
    debugLog("Started recording");
  }, [debugLog]);

  const stopRecording = useCallback(() => {
    isRecordingRef.current = false;
    setListeningStatus("");
    debugLog("Stopped recording");
  }, [debugLog]);

  // Stop game function - defined first to avoid reference issues
  const stopGame = useCallback(() => {
    debugLog("Stopping game...");

    if (isRecordingRef.current) {
      stopRecording();
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.close();
    }

    if (processorRef.current) {
      try {
        processorRef.current.disconnect();
      } catch (err) {
        debugLog(`Error disconnecting processor: ${err.message}`);
      }
      processorRef.current = null;
    }

    if (inputStreamRef.current) {
      try {
        inputStreamRef.current.disconnect();
      } catch (err) {
        debugLog(`Error disconnecting input stream: ${err.message}`);
      }
      inputStreamRef.current = null;
    }

    if (globalStreamRef.current) {
      try {
        globalStreamRef.current.getTracks().forEach((track) => track.stop());
      } catch (err) {
        debugLog(`Error stopping tracks: ${err.message}`);
      }
      globalStreamRef.current = null;
    }

    if (audioSourceRef.current) {
      try {
        audioSourceRef.current.stop();
      } catch (err) {
        debugLog(`Error stopping audio source: ${err.message}`);
      }
      audioSourceRef.current = null;
    }

    audioQueueRef.current = [];
    packetCounterRef.current = 0;
    isPlayingAudioRef.current = false;
    setIsSpeaking(false);

    setIsGameActive(false);
    setListeningStatus("");
    updateStatus("Game stopped");
    debugLog("Game stopped and cleaned up");
    stopTimer();
  }, [debugLog, stopRecording, stopTimer, updateStatus]);

  // Store stopGame in ref to avoid circular dependencies
  useEffect(() => {
    stopGameRef.current = stopGame;
  }, [stopGame]);

  // Audio playback function
  const playNextInQueue = useCallback(async () => {
    if (audioQueueRef.current.length === 0 || isPlayingAudioRef.current) {
      return;
    }

    isPlayingAudioRef.current = true;
    const audioData = audioQueueRef.current.shift();

    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext ||
          window.webkitAudioContext)();
      }

      const audioBuffer = await audioContextRef.current.decodeAudioData(
        audioData
      );
      const source = audioContextRef.current.createBufferSource();

      source.buffer = audioBuffer;
      source.connect(audioContextRef.current.destination);
      audioSourceRef.current = source;

      source.onended = () => {
        debugLog("Audio playback completed");
        audioSourceRef.current = null;
        isPlayingAudioRef.current = false;
        setIsSpeaking(false);

        // Notify server of completion if needed
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: "audio_completed" }));
        }

        // Start recording after a short delay
        setTimeout(() => {
          if (!isRecordingRef.current && isGameActive) {
            startRecording();
          }

          // Play next audio if any
          if (audioQueueRef.current.length > 0) {
            playNextInQueue();
          }
        }, 200);
      };

      debugLog("Starting audio playback");
      setIsSpeaking(true);
      source.start(0);
    } catch (error) {
      debugLog(`Error processing audio: ${error.message}`);
      audioSourceRef.current = null;
      isPlayingAudioRef.current = false;
      if (audioQueueRef.current.length > 0) {
        playNextInQueue();
      }
    }
  }, [debugLog, isGameActive, startRecording]);

  // Handle WebSocket messages - YOUR ORIGINAL LOGIC
  const handleWebSocketMessage = useCallback(
    (data) => {
      debugLog(`Processing message type: ${data.type}`);

      switch (data.type) {
        case "game_update":
          setScore(`${data.score}/${data.attempts}`);
          setUserAnswer(data.user_answer || "None");
          setCorrectAnswer(data.correct_answer || "None");
          setQuestion(data.question);
          break;

        case "game_question":
          setQuestion(data.question);
          break;

        case "game_over":
          if (data.should_stop_recording && isRecordingRef.current) {
            stopRecording();
            if (stopGameRef.current) {
              stopGameRef.current();
            }
          }
          setScore(`Final Score: ${data.score}/${data.attempts}`);
          setIsGameActive(false);
          break;

        case "text_chunk":
          setCurrentText((prev) => prev + data.text);
          break;

        default:
          break;
      }
    },
    [stopRecording, debugLog]
  );

  // Initialize audio capture
  const initializeAudioCapture = useCallback(async () => {
    try {
      debugLog("Initializing audio capture...");
      const sampleRate = 16000;

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: sampleRate,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      globalStreamRef.current = stream;

      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext ||
          window.webkitAudioContext)({
          sampleRate: sampleRate,
        });
      }

      inputStreamRef.current =
        audioContextRef.current.createMediaStreamSource(stream);
      processorRef.current = audioContextRef.current.createScriptProcessor(
        4096,
        1,
        1
      );

      processorRef.current.onaudioprocess = (e) => {
        if (
          isRecordingRef.current &&
          wsRef.current &&
          wsRef.current.readyState === WebSocket.OPEN
        ) {
          const inputData = e.inputBuffer.getChannelData(0);
          const audioData = encodePCM16(inputData);

          packetCounterRef.current++;
          const message = {
            type: "media",
            event: "media",
            ucid: ucidRef.current,
            data: {
              samples: Array.from(new Int16Array(audioData)),
              bitsPerSample: 16,
              sampleRate: sampleRate,
              channelCount: 1,
              numberOfFrames: inputData.length,
              type: "data",
            },
          };

          try {
            wsRef.current.send(JSON.stringify(message));
            debugLog(`Sent audio packet #${packetCounterRef.current}`);
          } catch (error) {
            debugLog(`Error sending audio: ${error.message}`);
          }
        }
      };

      inputStreamRef.current.connect(processorRef.current);
      processorRef.current.connect(audioContextRef.current.destination);
      debugLog("Audio capture initialized successfully");
      return true;
    } catch (error) {
      debugLog(`Error initializing audio: ${error.message}`);
      updateStatus("Microphone access denied or not available");
      return false;
    }
  }, [debugLog, encodePCM16, updateStatus]);

  // WebSocket setup
  const setupWebSocket = useCallback(() => {
    const wsUrl = `wss://sciencebee.vplaysmart.com/wsscience`;
    debugLog("Setting up WebSocket connection to: " + wsUrl);

    if (wsRef.current) {
      wsRef.current.close();
    }

    wsRef.current = new WebSocket(wsUrl);

    wsRef.current.onopen = () => {
      debugLog("WebSocket Connected");
      updateStatus("Connected! Starting game...");

      wsRef.current.send(
        JSON.stringify({
          event: "start",
          ucid: ucidRef.current,
          grade: queryParams.grade,
          topic: queryParams.topic,
        })
      );

      // Start the game once connected
      setIsGameActive(true);
      startTimer();
      startRecording();
    };

    wsRef.current.onmessage = async (event) => {
      try {
        debugLog(`Received message type: ${typeof event.data}`);

        if (event.data instanceof Blob) {
          debugLog("Received audio blob");
          const arrayBuffer = await event.data.arrayBuffer();
          audioQueueRef.current.push(arrayBuffer);
          if (!isPlayingAudioRef.current) {
            playNextInQueue();
          }
        } else {
          const message = JSON.parse(event.data);
          debugLog(`Received JSON message: ${JSON.stringify(message)}`);
          handleWebSocketMessage(message);
        }
      } catch (error) {
        debugLog(`Error processing message: ${error.message}`);
      }
    };

    wsRef.current.onerror = (error) => {
      debugLog(`WebSocket error: ${error}`);
      updateStatus("Connection error occurred");
      if (stopGameRef.current) {
        stopGameRef.current();
      }
    };

    wsRef.current.onclose = () => {
      debugLog("WebSocket Disconnected");
      updateStatus("Connection closed");
    };

    return wsRef.current;
  }, [
    debugLog,
    handleWebSocketMessage,
    playNextInQueue,
    startRecording,
    startTimer,
    updateStatus,
    queryParams,
  ]);

  // Start game function
  const startGame = useCallback(async () => {
    try {
      debugLog(
        `Using parameters: grade=${queryParams.grade}, topic=${queryParams.topic}`
      );
      updateStatus("Initializing audio...");

      const audioInitialized = await initializeAudioCapture();
      if (!audioInitialized) return;

      // Reset game state
      setUserAnswer("None");
      setCorrectAnswer("None");
      setQuestion("");
      setScore("0/0");
      setCurrentText("");

      updateStatus("Connecting to game server...");
      setupWebSocket();
    } catch (error) {
      debugLog(`Error in startGame: ${error.message}`);
      updateStatus("Failed to start game");
      if (stopGameRef.current) {
        stopGameRef.current();
      }
    }
  }, [
    debugLog,
    initializeAudioCapture,
    setupWebSocket,
    updateStatus,
    queryParams,
  ]);

  // Repeat question function
  const repeatQuestion = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      debugLog("Requesting question repeat");
      wsRef.current.send(JSON.stringify({ type: "repeat_question" }));
    }
  }, [debugLog]);

  // OAuth - Logout handler
  const handleLogout = useCallback(() => {
    if (window.confirm("Are you sure you want to logout?")) {
      if (isGameActive && stopGameRef.current) {
        stopGameRef.current();
      }
      oauthService.logout();
    }
  }, [isGameActive]);

  // Cleanup function
  useEffect(() => {
    return () => {
      if (stopGameRef.current) {
        stopGameRef.current();
      }
    };
  }, []);

  return (
    <div className="app-container" style={styles.container}>
      {/* 3D Avatar Section - Left Side */}
      <div className="avatar-section" style={styles.avatarSection}>
        <Canvas>
          <OrthographicCamera
            makeDefault
            zoom={800}
            position={[0.1, 1.55, 1]}
          />
          <Suspense fallback={null}>
            <Environment
              background={false}
              files="/images/photo_studio_loft_hall_1k.hdr"
            />
          </Suspense>
          <Suspense fallback={null}>
            <Avatar
              avatar_url="/model15.glb"
              isSpeaking={isSpeaking}
              currentText={currentText}
              audioContext={audioContextRef.current}
              audioSource={audioSourceRef.current}
            />
          </Suspense>
        </Canvas>
        <Loader dataInterpolation={(p) => `Loading... please wait`} />
      </div>

      {/* Science Bee Practice UI - Right Side */}
      <div className="ui-section" style={styles.uiSection}>
        <div style={styles.card}>
          <h1 style={styles.heading}>Science Bee Practice</h1>

          {/* OAuth - User Info Display */}
          {currentUser && (
            <div style={styles.userInfo}>
              👤 Logged in as:{" "}
              <strong>{currentUser.name || currentUser.username}</strong>
            </div>
          )}

          <div style={styles.timer}>{timer}</div>

          <div style={styles.buttonContainer}>
            <button
              onClick={startGame}
              disabled={isGameActive}
              style={{
                ...styles.button,
                ...(isGameActive ? styles.disabledButton : styles.startButton),
              }}
            >
              START
            </button>
            <button
              onClick={stopGame}
              disabled={!isGameActive}
              style={{
                ...styles.button,
                ...(!isGameActive ? styles.disabledButton : styles.stopButton),
              }}
            >
              STOP
            </button>
            <button
              onClick={repeatQuestion}
              disabled={!isGameActive}
              style={{
                ...styles.button,
                ...(!isGameActive
                  ? styles.disabledButton
                  : styles.repeatButton),
              }}
            >
              REPEAT
            </button>
            {/* OAuth - Logout Button */}
            <button
              onClick={handleLogout}
              style={{
                ...styles.button,
                ...styles.logoutButton,
              }}
            >
              LOGOUT
            </button>
          </div>

          {listeningStatus && (
            <div style={styles.listeningStatus}>{listeningStatus}</div>
          )}

          <div>
            <div style={styles.questionBox}>
              <h3 style={styles.boxTitle}>Question:</h3>
              <div dangerouslySetInnerHTML={{ __html: question }}></div>
            </div>

            <div style={styles.answerGrid}>
              <div style={styles.answerBox}>
                <h3 style={styles.boxTitle}>Your Answer:</h3>
                <div>{userAnswer}</div>
              </div>

              <div style={styles.answerBox}>
                <h3 style={styles.boxTitle}>Correct Answer:</h3>
                <div>{correctAnswer}</div>
              </div>
            </div>
          </div>

          {status && <div style={styles.statusText}>{status}</div>}

          <div style={styles.scoreDisplay}>Score: {score}</div>
        </div>
      </div>
    </div>
  );
}

// Main App with OAuth and Routing
function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* OAuth Callback Routes */}
        <Route path="/oauth/callback" element={<OAuthCallback />} />
        <Route path="/oauth/silent-callback" element={<SilentCallback />} />

        {/* Main App - Protected by OAuth */}
        <Route
          path="/*"
          element={
            <AuthWrapper>
              <ScienceBeeApp />
            </AuthWrapper>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
